/**
 * Shared mock order / contact payload for admin email previews only.
 * Apply never uses this — Go placeholders stay intact on save.
 *
 * Store-facing fields overlay live store config when present; otherwise defaults.
 */

import alienLabsGeminiThumb from "../figures/preview/alien-labs-gemini.png";
import caminoBloodOrangeThumb from "../figures/preview/camino-blood-orange.png";

const MOCK_FIRST_NAME = "John";
const MOCK_LAST_NAME = "Smith";
const MOCK_EMAIL = "john.smith@example.com";

export const PREVIEW_MOCK_ORDER = {
    CompanyName: "Storefront QA",
    // Person (contact + future template slots)
    FirstName: MOCK_FIRST_NAME,
    LastName: MOCK_LAST_NAME,
    Name: `${MOCK_FIRST_NAME} ${MOCK_LAST_NAME}`,
    Email: MOCK_EMAIL,
    Message: "Do you offer same-day pickup for edibles this weekend? Thanks!",
    DeliveryAddress: {
        FirstName: MOCK_FIRST_NAME,
        LastName: MOCK_LAST_NAME,
        Address: "123 Market St, Oakland, CA 94607",
    },
    OrderNumber: "TZ-10482",
    OrderType: "DELIVERY",
    Date: "Oct 17, 2026",
    OrderDetailsURL: "https://example.com/orders/TZ-10482",
    StoreDomain: "https://example.com",
    StoreDealsUrl: "https://example.com/deals",
    StoreAddress: "100 Treez Ave, Oakland, CA 94612",
    StoreEmail: "hello@example.com",
    StorePhoneNumber: "(510) 555-0199",
    Facebook: "https://facebook.com",
    Twitter: "https://x.com",
    Instagram: "https://instagram.com",
    Yelp: "https://yelp.com",
    LineItems: [
        {
            Name: "Alien Labs · Gemini",
            Gram: "3.5g",
            Quantity: 1,
            Price: 41.6,
            Thumbnail: alienLabsGeminiThumb,
        },
        {
            Name: "Camino · Blood Orange Spirited Sour Vegan Gummies",
            Gram: "100mg",
            Quantity: 1,
            Price: 20,
            Thumbnail: caminoBloodOrangeThumb,
        },
    ],
    SubtotalPrice: 61.6,
    TotalTax: 5.54,
    TotalServiceFee: 2.5,
    TotalDiscounts: 10.4,
    TotalPrice: 59.24,
};

const EMPTY_STORE_SENTINELS = new Set(["", "empty", "none", "n/a", "na", "-"]);

function trimStr(value) {
    return String(value ?? "").trim();
}

/** Store placeholders like "empty" are not real config — use mock default. */
function usableStoreValue(value) {
    const raw = trimStr(value);
    if (!raw) return "";
    if (EMPTY_STORE_SENTINELS.has(raw.toLowerCase())) return "";
    return raw;
}

function pickStoreValue(value, fallback) {
    return usableStoreValue(value) || fallback;
}

function normalizeStoreDomain(domain) {
    const raw = trimStr(domain);
    if (!raw) return "";
    const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    return withProtocol.replace(/\/+$/, "");
}

function formatStoreAddress(store) {
    if (!store || typeof store !== "object") return "";
    const street = [store.address1, store.address2]
        .map(usableStoreValue)
        .filter(Boolean)
        .join(" ");
    const cityState = [store.city, store.province_code]
        .map(usableStoreValue)
        .filter(Boolean)
        .join(", ");
    return [street, cityState, usableStoreValue(store.zip)]
        .filter(Boolean)
        .join(", ");
}

/**
 * Preview mock: store identity from config, sample customer/order otherwise.
 * @param {{ store?: object }} [options]
 */
export function getPreviewMockOrder({ store } = {}) {
    const source = store && typeof store === "object" ? store : {};
    const domain = normalizeStoreDomain(source.domain);
    const fallbackDomain = PREVIEW_MOCK_ORDER.StoreDomain;
    const storeDomain = domain || fallbackDomain;

    return {
        ...PREVIEW_MOCK_ORDER,
        CompanyName: pickStoreValue(
            source.name,
            PREVIEW_MOCK_ORDER.CompanyName,
        ),
        StoreDomain: storeDomain,
        StoreDealsUrl: `${storeDomain}/deals`,
        StoreAddress: pickStoreValue(
            formatStoreAddress(source),
            PREVIEW_MOCK_ORDER.StoreAddress,
        ),
        StoreEmail: pickStoreValue(source.email, PREVIEW_MOCK_ORDER.StoreEmail),
        StorePhoneNumber: pickStoreValue(
            source.phone_number,
            PREVIEW_MOCK_ORDER.StorePhoneNumber,
        ),
        Facebook: pickStoreValue(source.facebook, PREVIEW_MOCK_ORDER.Facebook),
        Twitter: pickStoreValue(source.twitter, PREVIEW_MOCK_ORDER.Twitter),
        Instagram: pickStoreValue(
            source.instagram,
            PREVIEW_MOCK_ORDER.Instagram,
        ),
        Yelp: pickStoreValue(source.yelp, PREVIEW_MOCK_ORDER.Yelp),
        OrderDetailsURL: domain
            ? `${domain}/orders/${PREVIEW_MOCK_ORDER.OrderNumber}`
            : PREVIEW_MOCK_ORDER.OrderDetailsURL,
    };
}
